import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, XCircle, X } from 'lucide-react';
import { useUsageStatus } from '../ui/UsageStatus';
import { useLanguage } from '../../context/LanguageContext';

const nf = (n) => Number(n || 0).toLocaleString('de-DE');
const fmtDay = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return ''; }
};
const dismissKey = (u) => `credits-warning-dismissed:${u?.plan || ''}:${u?.resetDate || 'none'}`;

/**
 * Hinweis über den Seiten: ab 80 % verbrauchter Credits eine Warnung (schließbar, kommt im nächsten Zyklus wieder),
 * bei 0 Credits ein Sperrhinweis (nicht schließbar), weil das Generieren dann blockiert ist.
 */
export default function CreditsNotice() {
  const { usage, limitReached, warning } = useUsageStatus();
  const { lang } = useLanguage();
  const T = (de, en) => (lang === 'de' ? de : en);
  const [dismissedTick, setDismissedTick] = useState(0);

  if (!usage || (!limitReached && !warning)) return null;

  let dismissed = false;
  try { dismissed = !limitReached && !!localStorage.getItem(dismissKey(usage)); } catch (e) { /* ohne Speicher: immer zeigen */ }
  if (dismissed || dismissedTick < 0) return null;

  const pct = Math.round(usage.percentUsed || 0);
  const reset = fmtDay(usage.resetDate);

  if (limitReached) {
    return (
      <div className="mb-4 lg:mb-6 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="credits-notice-empty" role="alert">
        <XCircle className="w-5 h-5 text-red-400 shrink-0" />
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-semibold text-red-300">{T('Deine Credits sind aufgebraucht.', 'You are out of credits.')}</div>
          <div className="text-xs text-red-300/80">
            {T('Das Generieren ist pausiert', 'Generating is paused')}
            {reset ? T(` bis zum nächsten Reset am ${reset}`, ` until the next reset on ${reset}`) : ''}
            {T(' oder bis du Credits hinzufügst.', ' or until you add credits.')}
          </div>
        </div>
        <Link to="/app/upgrade" className="text-sm font-semibold px-3 py-1.5 rounded bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4]">
          {T('Credits hinzufügen', 'Add credits')}
        </Link>
      </div>
    );
  }

  return (
    <div className="mb-4 lg:mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="credits-notice-warning" role="status">
      <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
      <div className="flex-1 min-w-[220px]">
        <div className="text-sm font-semibold text-amber-300">
          {T(`Du hast ${pct} % deiner Credits verbraucht.`, `You have used ${pct}% of your credits.`)}
        </div>
        <div className="text-xs text-amber-300/80">
          {T(`Noch ${nf(usage.remaining)} von ${nf(usage.limit)} übrig`, `${nf(usage.remaining)} of ${nf(usage.limit)} left`)}
          {reset ? T(`, Reset am ${reset}.`, `, resets on ${reset}.`) : '.'}
        </div>
      </div>
      <Link to="/app/upgrade" className="text-sm font-semibold px-3 py-1.5 rounded bg-amber-400 text-[#0A1628] hover:bg-amber-300">
        {T('Credits hinzufügen', 'Add credits')}
      </Link>
      <button
        onClick={() => { try { localStorage.setItem(dismissKey(usage), '1'); } catch (e) { /* egal */ } setDismissedTick((n) => n + 1); }}
        className="text-amber-300/70 hover:text-amber-200 p-1" aria-label={T('Schließen', 'Close')} data-testid="credits-notice-dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
